import type { ReactNode } from 'react';

import type { PlannedDependency } from '../summarizeInstall';
import { KindMonogram } from './KindMonogram';
import { StateBadge } from './StateBadge';

/** What each plan says, in the reader's words rather than the model's. */
const PLAN_WORDS = {
  add: 'will be added',
  stays: 'already here, stays as it is',
  missing: 'not in the library',
} as const;

/**
 * One of a workflow's dependencies, and what installing would do to it
 * (spec 105 FR-012).
 *
 * Before the install the row states the plan; afterwards it states the state,
 * because by then what happened is no longer a forecast. `settled` is which of
 * the two it is showing, and the drawer flips it one row at a time so the
 * install can be watched.
 */
export function DependencyRow({
  row,
  settled,
  children,
}: {
  row: PlannedDependency;
  /** True once the install has reported on this row. */
  settled: boolean;
  /** What this entry brings in itself, nested under it (spec 109 US2). */
  children?: ReactNode;
}) {
  const { dep, found, plan } = row;
  return (
    <li className="px-3 py-2 text-sm">
      <div className="flex items-center gap-3">
        <KindMonogram kind={dep.kind} size="sm" />
        <span className="min-w-0 flex-1 truncate">
          {found?.name ?? dep.key}
        </span>
        {settled && found ? (
          <StateBadge state={found.state} installable={found.installable} />
        ) : settled ? (
          <StateBadge
            state="not_installed"
            installable={false}
            reason="Not in the library."
          />
        ) : (
          <span className="text-muted-foreground text-xs whitespace-nowrap">
            {PLAN_WORDS[plan]}
          </span>
        )}
      </div>
      {children}
    </li>
  );
}
